import { useEffect, useState } from "react";
import { MessageSquare, Globe, Check, AlertCircle, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Compact "System Status" widget for the admin dashboard.
 * Shows live integration state for:
 *   • Twilio SMS (mock vs live) — with copy-paste instructions when in mock mode
 *   • Custom domain — with linking instructions
 */
export default function SystemStatus() {
    const [data, setData] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get("/admin/integrations")
            .then((r) => { if (alive) setData(r.data); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    if (!data) return null;

    const smsLive = data.sms?.live;
    const host = data.domain?.current_host || "";
    const isCustom = host && !host.includes("emergent");

    const envSnippet =
`TWILIO_ACCOUNT_SID=ACxxxxxxxxxxxxxxxxxxxxxx
TWILIO_AUTH_TOKEN=xxxxxxxxxxxxxxxxxxxxxxxx
TWILIO_FROM=+46xxxxxxxx`;

    const copyEnv = async () => {
        try {
            await navigator.clipboard.writeText(envSnippet);
            toast.success("Env snippet copied");
        } catch { /* noop */ }
    };

    return (
        <section
            className="rounded-sm border border-emerald-line/60 bg-bg-secondary/40 p-6 mb-8"
            data-testid="system-status"
        >
            <div className="flex items-center gap-3 mb-5">
                <span className="w-2 h-2 rounded-full bg-gold pulse-dot" />
                <span className="label-eyebrow">System Status</span>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
                {/* SMS */}
                <div
                    className={`relative rounded-sm border p-5 ${smsLive ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`}
                    data-testid="status-sms"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                            <MessageSquare size={16} className={smsLive ? "text-emerald-400" : "text-amber-400"} />
                            <div className="text-sm text-ink-primary font-medium">Approval SMS</div>
                        </div>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase border rounded-sm ${
                            smsLive
                                ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                                : "border-amber-500/40 text-amber-300 bg-amber-500/10"
                        }`}>
                            {smsLive ? <><Check size={10} /> Live</> : <><AlertCircle size={10} /> Mock</>}
                        </span>
                    </div>

                    {smsLive ? (
                        <p className="text-ink-secondary text-xs mt-3 leading-relaxed">
                            Twilio connected · sender <span className="text-gold font-mono">{data.sms.from_hint}</span>. Every approved member will get a real SMS.
                        </p>
                    ) : (
                        <>
                            <p className="text-ink-secondary text-xs mt-3 leading-relaxed">
                                Approvals log a mock SMS — no real texts are sent. Add these three vars to <code className="text-gold/90 font-mono text-[11px]">backend/.env</code> to go live:
                            </p>
                            <pre className="mt-3 p-3 rounded-sm bg-black/40 border border-emerald-line/60 text-[10.5px] leading-relaxed text-ink-secondary font-mono overflow-x-auto">
{envSnippet}
                            </pre>
                            <div className="mt-3 flex items-center gap-3">
                                <button
                                    onClick={copyEnv}
                                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-gold hover:text-gold-bright"
                                    data-testid="copy-env-snippet"
                                >
                                    <Copy size={11} /> Copy snippet
                                </button>
                                <a
                                    href="https://console.twilio.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-ink-secondary hover:text-gold"
                                >
                                    Get keys <ExternalLink size={10} />
                                </a>
                            </div>
                        </>
                    )}
                </div>

                {/* Domain */}
                <div
                    className={`relative rounded-sm border p-5 ${isCustom ? "border-emerald-500/40 bg-emerald-500/5" : "border-emerald-line/60 bg-bg-primary/60"}`}
                    data-testid="status-domain"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                            <Globe size={16} className={isCustom ? "text-emerald-400" : "text-ink-secondary"} />
                            <div className="text-sm text-ink-primary font-medium">Site Domain</div>
                        </div>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase border rounded-sm ${
                            isCustom
                                ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                                : "border-emerald-line text-ink-secondary bg-bg-secondary"
                        }`}>
                            {isCustom ? <><Check size={10} /> Custom</> : "Emergent"}
                        </span>
                    </div>
                    <p className="text-ink-secondary text-xs mt-3 leading-relaxed break-all">
                        Currently at <span className="text-gold font-mono">{host || "—"}</span>
                    </p>
                    {!isCustom && (
                        <>
                            <p className="text-ink-secondary text-xs mt-3 leading-relaxed">
                                To make guests find you by typing <span className="italic text-gold">johnscottsmedlem</span> in Google:
                            </p>
                            <ol className="mt-2 space-y-1.5 text-[11px] text-ink-secondary leading-relaxed list-decimal list-inside">
                                <li>Buy <code className="text-gold/90 font-mono">johnscottsmedlem.se</code> at a registrar (Loopia is easiest in Sweden).</li>
                                <li>In the Emergent deployment dashboard click <strong className="text-gold">&ldquo;Link domain&rdquo;</strong>.</li>
                                <li>In your registrar&apos;s DNS panel, <strong className="text-gold">remove all existing A-records</strong> first.</li>
                                <li>Wait 5–15 min for DNS · a few days for Google to index.</li>
                            </ol>
                        </>
                    )}
                </div>
            </div>
        </section>
    );
}
